/**
 * useNicepay Hook
 * FSD: features/subscription/checkout/hooks
 *
 * 나이스페이 결제 기능을 제공하는 커스텀 훅
 */

import { useState } from "react";
import {
  requestNicepayPayment,
  handleNicepayCallback,
  handleNicepayClose,
} from "../lib/nicepay.utils";
import type {
  NicepayPaymentRequest,
  NicepayPaymentResponse,
} from "../lib/nicepay.types";

interface UseNicepayReturn {
  isLoading: boolean;
  error: string | null;
  pay: (paymentData: NicepayPaymentRequest) => Promise<void>;
  onPaymentSuccess: (
    callback: (response: NicepayPaymentResponse) => void
  ) => void;
  onPaymentError: (callback: (error: any) => void) => void;
}

export const useNicepay = (): UseNicepayReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successCallback, setSuccessCallback] = useState<
    ((response: NicepayPaymentResponse) => void) | undefined
  >(undefined);
  const [errorCallback, setErrorCallback] = useState<
    ((error: any) => void) | undefined
  >(undefined);

  const pay = async (paymentData: NicepayPaymentRequest): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);

      // NICEPAY 결제창 요청
      requestNicepayPayment(paymentData);

      // 결제 완료 콜백 처리
      handleNicepayCallback(successCallback, errorCallback);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "결제 처리 중 오류가 발생했습니다";
      setError(errorMessage);
      errorCallback?.(err);
    } finally {
      setIsLoading(false);
    }
  };

  const onPaymentSuccess = (
    callback: (response: NicepayPaymentResponse) => void
  ): void => {
    setSuccessCallback(() => callback);
  };

  const onPaymentError = (callback: (error: any) => void): void => {
    setErrorCallback(() => callback);
  };

  return {
    isLoading,
    error,
    pay,
    onPaymentSuccess,
    onPaymentError,
  };
};

export default useNicepay;
